import { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import content from "./translations";
import CookieBanner from "./CookieBanner";
import LogoIntro from "./components/LogoIntro";
import Navigation from "./components/Navigation";
import Hero from "./components/Hero";
import MarqueeTicker from "./components/MarqueeTicker";
import Services from "./components/Services";
import About from "./components/About";
import FAQ from "./components/FAQ";
import ContactSection from "./components/ContactSection";
import Booking from "./components/Booking";
import Footer from "./components/Footer";
import WhatsAppWidget from "./components/WhatsAppWidget";

// Lazy load secondary routes to reduce initial bundle
const Blog = lazy(() => import("./Blog"));
const Impressum = lazy(() => import("./Impressum"));
const Barrierefreiheit = lazy(() => import("./Barrierefreiheit"));

function detectBrowserLanguage() {
  const browserLang = (navigator.language || navigator.userLanguage || "de").toLowerCase();
  if (browserLang.startsWith("fr")) return "FR";
  if (browserLang.startsWith("en")) return "EN";
  return "DE";
}

function detectTheme() {
  const saved = localStorage.getItem("undrive_theme");
  if (saved === "light" || saved === "dark") return saved;
  if (window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
  return "dark";
}

function LoadingFallback() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--blk, #1A1A1A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#E8952A', fontFamily: 'Barlow Condensed, sans-serif', fontSize: '1.4rem', fontWeight: 700 }}>Laden...</div>
    </div>
  );
}

function App() {
  const [showIntro, setShowIntro] = useState(true);
  const [lang, setLang] = useState(() => detectBrowserLanguage());
  const [theme, setTheme] = useState(() => detectTheme());
  const [scrollY, setScrollY] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const t = content[lang];

  // Update HTML lang attribute dynamically
  useEffect(() => {
    document.documentElement.lang = lang.toLowerCase();
  }, [lang]);

  useEffect(() => {
    if (location.pathname !== '/') {
      setShowIntro(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".fade-in, .fade-in-left, .fade-in-right, .fade-in-scale").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [showIntro, location.pathname]);

  // Skip intro if user prefers reduced motion
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShowIntro(false);
    }
  }, []);

  if (showIntro && location.pathname === '/') {
    return <LogoIntro onComplete={() => setShowIntro(false)} />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/blog/*" element={<Blog lang={lang} onBack={() => navigate('/')} />} />
        <Route path="/impressum" element={<Impressum onBack={() => navigate('/')} lang={lang} />} />
        <Route path="/barrierefreiheit" element={<Barrierefreiheit onBack={() => navigate('/')} lang={lang} />} />
        <Route path="*" element={
          <div className="app" data-testid="main-app">
            <a href="#main-content" className="skip-link" data-testid="skip-link">
              {lang === "DE" ? "Zum Inhalt springen" : lang === "EN" ? "Skip to content" : "Aller au contenu"}
            </a>
            <Navigation lang={lang} setLang={setLang} onShowBlog={() => navigate('/blog')} theme={theme} setTheme={setTheme} />
            <main id="main-content">
              <Hero t={t} scrollY={scrollY} />
              <MarqueeTicker t={t} />
              <Services t={t} />
              <About t={t} />
              <FAQ t={t} />
              <ContactSection lang={lang} t={t} />
              <Booking t={t} />
            </main>
            <Footer t={t} lang={lang} navigate={navigate} />
            <WhatsAppWidget lang={lang} />
            <CookieBanner lang={lang} />
          </div>
        } />
      </Routes>
    </Suspense>
  );
}

export default App;
